// Sidebar.js – behavior and layout match TableMateBackEndServerApp sidebar
import React, { useEffect, useState } from "react";
import "./sidebar.css";
import logo from "../Assets/logo.png";
import searchButton from "../Assets/Menu/search-button.png";
import helpButton from "../Assets/Menu/user_white.png";
import homeButton from "../Assets/Menu/home.png";
import { useLocation, Link } from "react-router-dom";
import { api } from "../api";

const NAV_ITEMS = [
  { title: "Home", icon: homeButton, link: "/" },
  { title: "Search", icon: searchButton, link: "/search" },
  { title: "Contact", icon: helpButton, link: "/contact" },
];

const DRAWER_BREAKPOINT = "(max-width: 1023px)";

function Sidebar({ isOpen, onClose }) {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const [quickLinks, setQuickLinks] = useState([]);
  const [quickLinksLoading, setQuickLinksLoading] = useState(true);

  useEffect(() => {
    setQuickLinksLoading(true);
    api.get("/api/quick_links.php")
      .then((data) => {
        setQuickLinks(Array.isArray(data) ? data : []);
        setQuickLinksLoading(false);
      })
      .catch(() => {
        setQuickLinks([]);
        setQuickLinksLoading(false);
      });
  }, []);

  // Backdrop only on mobile when menu is open (triggered by .icon-button)
  useEffect(() => {
    const mq = window.matchMedia(DRAWER_BREAKPOINT);
    const update = () => setIsMobileView(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  // Default collapsed on mobile (except /login if ever used)
  useEffect(() => {
    if (location.pathname === "/login") return;

    const isMobile = window.matchMedia("(max-width: 768px)").matches;

    if (isMobile) {
      setIsCollapsed(true);
      document.documentElement.style.setProperty("--sidebar-w", "40px");
    } else {
      setIsCollapsed(false);
      document.documentElement.style.setProperty("--sidebar-w", "355px");
    }
  }, [location.pathname]);

  const toggleSidebarWidth = (e) => {
    e?.stopPropagation?.();
    setIsCollapsed((prev) => {
      const next = !prev;
      document.documentElement.style.setProperty(
        "--sidebar-w",
        next ? "40px" : "355px"
      );
      return next;
    });
  };

  const handleOverlayClick = () => {
    if (isOpen) onClose?.();
  };

  // Only close sidebar on link click when in mobile/drawer mode; keep open on desktop
  const closeIfMobile = () => {
    if (isMobileView) onClose?.();
  };

  const handleNavClick = () => {
    closeIfMobile();
  };

  const handleHomeClick = () => {
    closeIfMobile();
  };

  const handleSearchClick = () => {
    closeIfMobile();
    setTimeout(() => {
      document.querySelector(".search-bar")?.focus();
    }, 150);
  };

  const showBackdrop = isOpen && isMobileView;

  return (
    <div>
      {/* Grey overlay only on mobile when menu is open (opened via .icon-button) */}
      {showBackdrop && (
        <div
          className="sidebar-backdrop"
          onClick={handleOverlayClick}
          onKeyDown={(e) => e.key === "Escape" && onClose?.()}
          role="button"
          tabIndex={0}
          aria-label="Close sidebar"
        />
      )}
      <div
        className={`sidebar-content overlay ${
          isCollapsed ? "sidebar-collapsed" : ""
        }${isOpen ? " sidebar-drawer-open" : ""}`}
        style={{ display: isOpen ? "block" : "none" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sidebar-top" onClick={(e) => e.stopPropagation()}>
          <div className="tm-logo">
            <img src={logo} alt="Custom Logo" />
            <div className="header-info">
              <p className="company-name">Table Mate</p>
              <p className="site-title">Documentation</p>
            </div>

            <button
              type="button"
              className="sidebar-collapse-btn"
              onClick={toggleSidebarWidth}
              aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              title={isCollapsed ? "Expand" : "Collapse"}
            >
              {isCollapsed ? "»" : "«"}
            </button>
          </div>
        </div>

        <div className="sidebar-nav" onClick={(e) => e.stopPropagation()}>
          <ul className="sidebar-links">
            {NAV_ITEMS.map((item) => (
              <li key={item.link}>
                <Link
                  to={item.link}
                  className="sidebar-nav-item"
                  onClick={
                    item.link === "/"
                      ? handleHomeClick
                      : item.link === "/search"
                        ? handleSearchClick
                        : handleNavClick
                  }
                >
                  <div className="side-nav-item-icon">
                    <img
                      src={`${item.icon}`}
                      alt={`${item.icon} icon`}
                    />
                  </div>
                  <h4 className="sidebar-nav-item-title">{item.title}</h4>
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {!isCollapsed && (
          <div className="sidebar-bottom" onClick={(e) => e.stopPropagation()}>
            <h3 className="sidebar-heading">Quick links</h3>
            {quickLinksLoading || quickLinks.length === 0 ? (
              <p className="sidebar-quick-links-placeholder">Loading...</p>
            ) : (
              <ul className="sidebar-frequently-searched">
                {quickLinks.map((item) => {
                  const url = item.url || "";
                  let isInternal = url.startsWith("/");
                  let to = url;
                  if (!isInternal && url.startsWith("http")) {
                    try {
                      const u = new URL(url);
                      isInternal = u.hostname === window.location.hostname;
                      if (isInternal) to = u.pathname + u.search;
                    } catch (_) {}
                  }
                  return (
                    <li key={item.id || item.label}>
                      {url && (isInternal ? (
                        <Link to={to} onClick={closeIfMobile}>{item.label}</Link>
                      ) : (
                        <a href={url} target="_blank" rel="noopener noreferrer">{item.label}</a>
                      ))}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Sidebar;
